import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Variable } from '@restworld/utility-types';
import { cn, listIconType } from '@restworld/utils-common';

import { DescriptionBlock } from '../job-description-block';
import { JobContract } from '../job-contract';
import { JobShifts } from '../job-shifts';
import { MapComponent } from '../job-location-map';
import { Wrapper, ListTertiary, ImageCarousel } from '../../design-system';
import { Heading, Text } from '@restworld/ui-ds-base';
import { HandThumbUpIcon, MapIcon } from '@heroicons/react/24/outline';
import { JobPositionPreviewProps, JobPositionPreviewBlockProps } from './types';
import { usePerformanceBlockData, useSkillsBlockData } from './hooks';
import { SkillsPreviewBlock } from './skills-preview-block';
import { JobInfoCardPreview } from './job-info-card-preview';

/**
 * JobPositionPreview - Componente per mostrare l'anteprima di una posizione lavorativa
 * Utilizzato nell'employer-app per mostrare come i candidati vedranno la job position
 *
 * @param jobPosition - I dati della job position da visualizzare
 */
export const JobPositionPreview = ({ jobPosition }: JobPositionPreviewProps) => {
  if (!jobPosition) return null;

  return (
    <PageContentWrapper>
      {/* Mobile: JobInfoCard in alto */}
      <MobileJobInfoCard>
        <JobInfoCardPreview jobPosition={jobPosition} />
      </MobileJobInfoCard>

      {/* Desktop: Card a destra (grazie a flex-row-reverse), contenuti a sinistra */}
      <DesktopJobInfoCard>
        <JobInfoCardPreview jobPosition={jobPosition} />
      </DesktopJobInfoCard>

      {/* Contenuti principali */}
      <MainContentColumn>
        <JobDescriptionPreview jobPosition={jobPosition} />
        <ContractPreview jobPosition={jobPosition} />
        <PerformancePreview jobPosition={jobPosition} />
        {/* <WhyTrustUsBlock /> */}
        <SkillsPreview jobPosition={jobPosition} />
        <ShiftsPreview jobPosition={jobPosition} />
        <LocationPreview jobPosition={jobPosition} />
      </MainContentColumn>
    </PageContentWrapper>
  );
};

// Descrizione del lavoro
const JobDescriptionPreview = ({ jobPosition }: JobPositionPreviewBlockProps) => {
  if (!jobPosition?.job_description) return null;

  return (
    <>
      <DescriptionBlock subTitle={jobPosition.job_title} jobContractDesc={jobPosition.job_description} />
      <Divider />
    </>
  );
};

// Contratto
const ContractPreview = ({ jobPosition }: JobPositionPreviewBlockProps) => {
  const contractContent = jobPosition?.job_contract_description;
  const jobBenefitsList = useMemo(
    () =>
      jobPosition?.benefits?.map(({ benefit_var }: { benefit_var: Variable }) => ({
        label: benefit_var.label,
        icon: benefit_var.icon as listIconType,
        key: benefit_var.key,
      })),
    [jobPosition?.benefits]
  );

  if (!contractContent?.length) return null;

  return (
    <>
      <JobContract contractContent={contractContent} jobBenefits={jobBenefitsList} />
      <Divider />
    </>
  );
};

// Performance/Valutazioni
const PerformancePreview = ({ jobPosition }: JobPositionPreviewBlockProps) => {
  const { t } = useTranslation('common');
  const jobPerformanceItemList = usePerformanceBlockData(jobPosition);

  if (!jobPerformanceItemList.length) return null;

  return (
    <>
      <Wrapper>
        <span className="flex items-center gap-1">
          <HandThumbUpIcon className="h-6 w-6" />
          <Heading size={4} weight="semibold">
            {t('our_advice')}
          </Heading>
        </span>
        <ListTertiary jobPerformanceItemList={jobPerformanceItemList} />
      </Wrapper>
      <Divider />
    </>
  );
};

// Skills
const SkillsPreview = ({ jobPosition }: JobPositionPreviewBlockProps) => {
  const skillsData = useSkillsBlockData(jobPosition);

  if (skillsData.isEmptyData) return null;

  return (
    <>
      <SkillsPreviewBlock {...skillsData} />
      <Divider />
    </>
  );
};

// Turni
const ShiftsPreview = ({ jobPosition }: JobPositionPreviewBlockProps) => {
  const jobShiftsArgs = useMemo(
    () => ({
      jobType: jobPosition?.job_type_var?.label || '',
      daysRequiredPerWeek: jobPosition?.days_required_per_week,
      daysRequiredPerWeekNumber: jobPosition?.days_required_per_week_number,
      shifts: jobPosition?.shifts,
      shiftNotes: jobPosition?.shift_notes || '',
    }),
    [
      jobPosition?.days_required_per_week,
      jobPosition?.days_required_per_week_number,
      jobPosition?.job_type_var?.label,
      jobPosition?.shift_notes,
      jobPosition?.shifts,
    ]
  );

  const isEmptyEl = useMemo(
    () =>
      Boolean(
        !jobPosition?.days_required_per_week?.length &&
          !jobPosition?.days_required_per_week_number &&
          !jobPosition?.shifts?.length
      ),
    [
      jobPosition?.days_required_per_week?.length,
      jobPosition?.days_required_per_week_number,
      jobPosition?.shifts?.length,
    ]
  );

  if (isEmptyEl) return null;

  return (
    <>
      <JobShifts {...jobShiftsArgs} />
      <Divider />
    </>
  );
};

// Località
const LocationPreview = ({ jobPosition }: JobPositionPreviewBlockProps) => {
  const { t } = useTranslation('common');
  const restaurant = jobPosition?.restaurant;

  const mapProps = useMemo(
    () => ({
      showRestaurant: jobPosition?.show_restaurant_details || false,
      lat: restaurant?.address_lat || 0,
      lon: restaurant?.address_lon || 0,
      approximateLat: restaurant?.approximate_lat || 0,
      approximateLon: restaurant?.approximate_lon || 0,
    }),
    [
      jobPosition?.show_restaurant_details,
      restaurant?.address_lat,
      restaurant?.address_lon,
      restaurant?.approximate_lat,
      restaurant?.approximate_lon,
    ]
  );

  return (
    <Wrapper>
      <div className="flex flex-col gap-0">
        <div className="mb-4">
          <span className="flex items-center gap-1">
            <MapIcon className="h-6 w-6" />
            <Heading size={4} weight="semibold">
              {t('job_position_preview_location_title')}
            </Heading>
          </span>
        </div>
        <MapComponent {...mapProps} />
        {restaurant?.location_description && <Text>{restaurant.location_description}</Text>}
      </div>

      {restaurant?.pictures?.length && mapProps.showRestaurant ? (
        <>
          <Heading as="span" size={5} weight="semibold">
            {t('job_position_preview_venue_presentation_title')}
          </Heading>
          <Text>{restaurant.brief_description}</Text>
          <ImageCarousel restaurantPictures={restaurant.pictures.map((v: { url: string }) => v.url)} />
        </>
      ) : null}
    </Wrapper>
  );
};

// Layout Components
// Replica il layout della worker-app: flex-row-reverse mette la card a destra su desktop
const PageContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('w-full flex flex-col gap-8 lg:flex lg:flex-row-reverse lg:justify-center lg:gap-10', className)}
    {...props}
  />
);

// Mobile: mostra la card in cima
const MobileJobInfoCard = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('block lg:hidden', className)} {...props} />
);

// Desktop: card sticky a destra (appare prima grazie a flex-row-reverse)
const DesktopJobInfoCard = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('hidden lg:block lg:w-96 lg:min-w-[384px]', className)} {...props} />
);

// Colonna principale con i contenuti (appare a sinistra su desktop)
const MainContentColumn = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col gap-8 w-full 2xl:max-w-[calc(100vw-864px)] xl:max-w-[calc(100vw-664px)] lg:max-w-[calc(100vw-520px)]',
      className
    )}
    {...props}
  />
);

const Divider = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <hr className={cn('text-gray-300', className)} {...props} />
);

export default JobPositionPreview;
